import { useState, useCallback, useEffect } from 'react';
import apiClient from '../../../api/core/apiClient';
import { blobCache, activeRequests } from '../WhatsappModule';

export type MediaStatus = 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR';

// Simple IndexedDB helpers to persist blobs across reloads
const DB_NAME = 'whatsapp-media-cache';
const DB_VERSION = 2; // Must match WhatsappModule.tsx
const STORE_NAME = 'blobs';

interface BlobMetadata {
    blob: Blob;
    timestamp: number;
}

function openMediaDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key: string): Promise<Blob | null> {
    try {
        const db = await openMediaDB();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(key);
            req.onsuccess = () => {
                const result = req.result;
                // Handle both old format (direct Blob) and new format (BlobMetadata)
                if (result) {
                    resolve((result as BlobMetadata).blob || (result as Blob));
                } else {
                    resolve(null);
                }
            };
            req.onerror = () => reject(req.error);
        });
    } catch {
        return null;
    }
}

async function idbSet(key: string, value: Blob): Promise<void> {
    try {
        const db = await openMediaDB();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const metadata: BlobMetadata = {
                blob: value,
                timestamp: Date.now()
            };
            const req = store.put(metadata, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch {
        // ignore persistence errors
    }
}

export const useMediaAttachment = (mediaId?: string) => {
    const hasCached = !!mediaId && blobCache.has(mediaId);
    const [url, setUrl] = useState<string | null>(hasCached ? blobCache.get(mediaId)! : null);
    const [status, setStatus] = useState<MediaStatus>(hasCached ? 'SUCCESS' : 'IDLE');

    // On mount check persistent cache (IndexedDB) if not in memory yet
    useEffect(() => {
        let cancelled = false;
        const checkPersistent = async () => {
            if (!mediaId) return;
            if (blobCache.has(mediaId)) return; // already in-memory
            // indicate we are checking to avoid flashing a download button
            setStatus((s) => (s === 'IDLE' ? 'LOADING' : s));
            const blob = await idbGet(mediaId);
            if (cancelled) return;
            if (blob) {
                const objectUrl = URL.createObjectURL(blob);
                blobCache.set(mediaId, objectUrl);
                setUrl(objectUrl);
                setStatus('SUCCESS');
            } else {
                // Not found in persistent cache, revert to idle
                setStatus((s) => (s === 'LOADING' ? 'IDLE' : s));
            }
        };
        checkPersistent();
        return () => {
            cancelled = true;
        };
    }, [mediaId]);

    const downloadMedia = useCallback(async () => {
        if (!mediaId) return;
        
        if (blobCache.has(mediaId)) {
            setUrl(blobCache.get(mediaId)!);
            setStatus('SUCCESS');
            return;
        }

        setStatus('LOADING');

        try {
            let request = activeRequests.get(mediaId);
            if (!request) {
                request = apiClient.get<Blob>(`/whatsapp/messages/media/${mediaId}`, {}, {
                    responseType: 'blob'
                }) as Promise<Blob>;
                activeRequests.set(mediaId, request);
            }

            const blob = await request;
            // Persist in IndexedDB for future reloads
            idbSet(mediaId, blob).catch(() => {});
            const objectUrl = URL.createObjectURL(blob);
            blobCache.set(mediaId, objectUrl);
            setUrl(objectUrl);
            setStatus('SUCCESS');
        } catch (error) {
            console.error('Error fetching media:', error);
            setStatus('ERROR');
            activeRequests.delete(mediaId);
        }
    }, [mediaId]);

    return { url, status, downloadMedia };
};
